import * as Sentry from "@sentry/react";
import React, { Component, ErrorInfo, ReactNode } from "react";

import { PrimaryButton } from "../Buttons/StyledButtons";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidMount() {
    // Catch unhandled Promise rejections (API errors)
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
  }

  componentWillUnmount() {
    // Remove unhandled rejection listener
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
  }

  // Handles unhandled Promise rejections (API errors, async failures)
  // These errors don't break the UI but should be logged for monitoring
  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    // Log the error
    console.error("Unhandled Promise Rejection:", event.reason);

    // Send to Sentry
    Sentry.captureException(event.reason, {
      tags: {
        component: "ErrorBoundary",
        errorType: "unhandledPromiseRejection",
      },
    });

    // Prevent the default browser behavior
    event.preventDefault();
  };

  // Handles React render errors (component crashes, render failures)
  // These errors break the UI and trigger the ErrorBoundary fallback
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log the error to console for development
    if (import.meta.env.DEV) {
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    // Send to Sentry for error tracking
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
      tags: {
        component: "ErrorBoundary",
      },
    });
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="flex-1 bg-gray-50 flex flex-col justify-center items-center px-8 w-full h-full">
          <div className="flex flex-col items-center text-center max-w-md">
            <h2
              className="font-bold text-2xl text-gray-900"
              data-testid="error-heading"
            >
              Oops! Something Went Wrong
            </h2>
            <p
              className="pt-5 text-gray-700 leading-relaxed text-base font-light mb-4"
              data-testid="error-message"
            >
              We&apos;re sorry, a system error has occurred.
              <br />
              If the problem persists, try signing out and back in.
            </p>
            <p
              className="text-gray-700 text-base font-light mb-8"
              data-testid="error-support-text"
            >
              Contact Support for{" "}
              <a
                href="mailto:support@rebootmotion.com"
                className="text-blue-600 underline hover:text-blue-800"
              >
                help
              </a>
              .
            </p>
            <PrimaryButton
              onClick={() => (window.location.href = "/")}
              data-testid="error-home-link"
            >
              Go to Home
            </PrimaryButton>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
